import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { ReplaySubject, combineLatest, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { HOST_CHAT, MODERATED_CHAT } from 'entity/SocialSettings';
import { ChatStatus } from 'messages/websocket.messages';

import { ChatService } from '../../services/chat.service';
import { socialAppActions, socialAppSelectors } from '../../state/app';

@Component({
  selector: 'app-social-chat',
  templateUrl: './social-chat.component.html',
  styleUrls: ['./social-chat.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialChatComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  protected chatService = inject(ChatService);

  private destroyed$ = new ReplaySubject<boolean>(1);

  protected readonly isHost = signal(false);
  protected readonly isPresenting = signal(false);
  protected readonly moderatedChat = signal(false);
  protected readonly hostChatSetting = signal(false);
  protected readonly chatModerationSetting = signal(false);
  protected readonly isUserHost = signal(false);

  public readonly tabSelected = signal<string | undefined>(undefined);

  protected get seesRaisedHands(): boolean {
    return this.isHost() || this.isPresenting();
  }

  public constructor() {
    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresenting) => this.isPresenting.set(isPresenting));

    combineLatest([
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.moderated_chat),
      this.store.select(socialAppSelectors.getRoomSettings),
      this.store.select(socialAppSelectors.isHost),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([moderatedChat, socialSettings, isUserHost]) => {
        this.moderatedChat.set(moderatedChat);
        this.isUserHost.set(isUserHost);

        if (moderatedChat && socialSettings && socialSettings.setSettings) {
          this.hostChatSetting.set(socialSettings.setSettings[HOST_CHAT] ? true : false);
          this.chatModerationSetting.set(socialSettings.setSettings[MODERATED_CHAT] ? true : false);
        }
      });
  }

  public ngOnInit(): void {
    this.store.dispatch(socialAppActions.TOGGLE_CHAT({ enabled: true }));
  }

  public ngOnDestroy(): void {
    this.store.dispatch(socialAppActions.TOGGLE_CHAT({ enabled: false }));
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public selectedTabChange(tabChangeEvent: MatTabChangeEvent): void {
    this.tabSelected.set(tabChangeEvent.tab.textLabel);
  }

  public readonly pendingMessageCount = computed(() => {
    const pendingMessagesCount = _.filter(this.chatService.moderationFeed(), { status: ChatStatus.PENDING }).length;
    return pendingMessagesCount;
  });

  public readonly showCountMessage = computed(() => {
    return this.pendingMessageCount() > 0;
  });
}
